import React, { createRef, useEffect, useMemo, useRef, useState } from 'react';
// @ts-ignore
import { Header, HeaderProps } from '../../components/header';
// @ts-ignore
import Footer from '../../components/footer';
import Sidebar from '../../components/sidebar';
import TableOfContents from '../../components/sidebar/table-of-contents';
import RightNav from '../../components/sidebar/right-nav';
import Community from '../../components/sidebar/community';
// @ts-ignore
import DeploymentSelector from '../../components/deployment-selector';
// @ts-ignore
import AdditionalHelp from '../../components/additional-help';
// @ts-ignore
import FeedbackButtons from '../../components/feedback-buttons';
// @ts-ignore
import Breadcrumbs from '../../components/breadcrumbs';
import {
    PageTree,
    PageTreeList,
    PageTreeItem
} from '../../components/page-tree';
import './topic.less';
// @ts-ignore
import AuthorTools from '../../components/author-tools';
// @ts-ignore
import { TOPIC } from '../../../contentful/content-types';
// @ts-ignore
import { CardGroup, CardWithCEM } from '../../components/card';
import { CollectionTreeType } from '../../components/sidebar/sidebar.types';
import { getUpNextContent } from './util/get-up-next-content';
import {
    TopicSac,
    isProductContext,
    isThemeContext
} from '../../../content/types/content';
import {
    getContextName,
    getSupportTitle
} from '../../../content/context-utils';
import NoticeHub from '../../components/notice-hub';
import ComponentVideo from '../../components/component-video';
import { useContentfulLiveUpdates } from '../../../contentful/live-preview-react';
// @ts-ignore
import AllIcons from '../../../adf-extensions/icon/utils/all-icons';
import {
    getExtensionHandlers,
    type PositionTracker
} from '../../components/extensions/extension-handlers';
import { useScrollDepthAnalytics } from '../../hooks/use-scroll-depth-analytics';
import { useDwellTimeAnalytics } from '../../hooks/use-dwell-time-analytics';
import { useContentViewedAnalytics } from '../../hooks/use-content-viewed-analytics';
import {
    trackContentLinkClick,
    trackContentLinkViewed,
    type ContentLinkAnalyticsPayload
} from '../../components/atlas-link/track';
// @ts-ignore
import analytics from '../../utils/analytics';
import { resolveAnalyticsUrl } from '../../utils/content-analytics-resources';

/**
 * Handle embedded link clicks via event delegation using existing analytics helpers
 */
const handleEmbeddedLinkClick = (e: Event) => {
    const targetElement = e.target as Element | null;
    const anchor = targetElement?.closest?.('a');

    if (!anchor || !(anchor instanceof HTMLAnchorElement) || !anchor.href) {
        return;
    }

    try {
        // Get position from data attribute set during view tracking
        const positionStr = anchor.getAttribute('data-link-position');
        const position = positionStr ? Number.parseInt(positionStr, 10) : 0;

        const entryAri = anchor.getAttribute('data-entry-ari');

        if (entryAri) {
            // Use the shared analytics helper when we have full metadata
            const analyticsPayload: ContentLinkAnalyticsPayload = {
                linkType: 'text-link',
                context: 'reference',
                position,
                linkContentAri: entryAri,
                linkContentResolvedUrl: anchor.href,
                parentPageType: 'topic'
            };

            trackContentLinkClick({
                href: anchor.href,
                contentLinkAnalytics: {
                    payload: analyticsPayload,
                    shouldSkip: false
                }
            });
            return;
        }

        // Fallback: maintain legacy analytics behavior when metadata is missing
        analytics.trackEvent('content-link clicked', {
            event: 'clicked',
            eventComponent: 'content-link',
            eventContainer: 'reference',
            position,
            link_type: 'text-link',
            link_content_resolved_url: resolveAnalyticsUrl(anchor.href),
            parent_page_type: 'topic',
            link_content_ari: anchor.getAttribute('data-entry-ari') || null
        });
    } catch (error) {
        // Log error but don't break rendering
        // eslint-disable-next-line no-console
        console.warn('Failed to track embedded link click', {
            href: anchor.href,
            error
        });
    }
};

export type TopicPageEntry = TopicSac & {
    header: HeaderProps;
    preview: boolean;
    resources: any;
};

interface SACTopicPageProps {
    entry: TopicPageEntry;
    pageTree: CollectionTreeType[];
    featureFlags: {
        isVideoEnabled: boolean;
    };
}

const SACTopicPage = ({ entry, pageTree, featureFlags }: SACTopicPageProps) => {
    const updatedEntry = useContentfulLiveUpdates(entry, {
        skip: !entry.preview
    });

    const {
        topicTitle,
        body,
        header,
        id,
        preview,
        tableOfContents,
        versions,
        context,
        collectionId,
        topicSetId,
        url,
        resources,
        pageType,
        confluencePageId,
        collections,
        relatedAppReferences,
        featuredVideo,
        extensionsData,
        type
    } = updatedEntry;

    const { isVideoEnabled } = featureFlags;

    // Create persistent position tracker for extension links
    // Reset when entry ID changes (new page/navigation)
    const positionTrackerRef = useRef<PositionTracker>({ sacLinkPosition: 0 });

    // Reset position tracker when navigating to a new entry
    useEffect(() => {
        positionTrackerRef.current = { sacLinkPosition: 0 };
    }, [id]);

    const subContentRef = createRef<HTMLDivElement>();

    const isProgressBarEnabled = !!topicSetId && pageType === 'Guide';

    const collectionTree = pageTree.find(
        (collection) => collection.id === collectionId
    );

    // Extract topic set metadata when available
    const topicSet = collectionTree?.childList?.find(
        (topicSet) => topicSet.id === topicSetId
    );
    const topicSetUrl = topicSet?.slug;

    // Extract collection metadata for fallback
    const collectionUrl = collectionTree?.slug;

    const { prevGuideCard, nextGuideCard, otherGuideTopicSets } =
        useMemo(() => {
            return getUpNextContent(
                {
                    topicTitle,
                    body,
                    id,
                    collectionId,
                    topicSetId
                },
                isProgressBarEnabled,
                pageTree
            );
        }, [
            isProgressBarEnabled,
            topicSetId,
            id,
            body,
            collectionId,
            pageTree,
            topicTitle
        ]);

    const productName = getContextName(context);

    const [updatedBody, setUpdatedBody] = useState<JSX.Element>();
    const [allIcons, setAllIcons] = useState<any>(null);
    // Ref to body content container - used to query embedded links for analytics tracking (viewed/clicked events)
    const [bodyContentRef, setBodyContentRef] = useState<HTMLDivElement | null>(
        null
    );

    // Render the Topic body using @atlaskit/renderer on the client side for Contentful live preview
    useEffect(() => {
        if (!preview) {
            return;
        }

        (async () => {
            // @ts-ignore
            const { ReactRenderer } = await import('@atlaskit/renderer');
            // @ts-ignore
            const { IntlProvider } = await import('react-intl');

            if (!allIcons) {
                setAllIcons(await AllIcons());
            }

            const extensionHandlers = getExtensionHandlers(
                {
                    ...extensionsData,
                    icon: {
                        hideToolTips: true,
                        allIcons
                    }
                },
                positionTrackerRef.current
            );
            const adfBody =
                // @ts-ignore
                type === TOPIC ? body : updatedEntry?.derivedFrom?.body;

            const bodyRenderedAdf = (
                <IntlProvider locale="en">
                    <ReactRenderer
                        document={adfBody}
                        extensionHandlers={extensionHandlers}
                    />
                </IntlProvider>
            );
            setUpdatedBody(bodyRenderedAdf);
        })();
    }, [allIcons, body, extensionsData, preview, type, updatedEntry]);

    /**
     * Track "viewed" events for embedded links when body content changes
     */
    useEffect(() => {
        if (preview || !bodyContentRef || !body) {
            return;
        }

        // Debounce to ensure DOM is fully rendered
        const timer = setTimeout(() => {
            const embeddedLinks = bodyContentRef.querySelectorAll('a');
            embeddedLinks.forEach((link, index) => {
                const position = index; // 0-based position
                const href = link.href;

                try {
                    // Store position as data attribute for click handler to retrieve
                    // This ensures view and click events use the same position value
                    link.dataset.linkPosition = String(position);

                    const entryAri = link.getAttribute('data-entry-ari');
                    const entryId = link.getAttribute('data-entry-id');

                    trackContentLinkViewed({
                        linkType: 'text-link',
                        context: 'reference',
                        position,
                        linkContentAri: entryAri || undefined,
                        linkContentId: entryId || undefined,
                        linkContentResolvedUrl: href,
                        parentPageType: 'topic'
                    });
                } catch (error) {
                    // Log error but don't break rendering
                    // eslint-disable-next-line no-console
                    console.warn('Failed to track embedded link view', {
                        href,
                        error
                    });
                }
            });
        }, 100);

        return () => clearTimeout(timer);
    }, [preview, bodyContentRef, body, id, type]);

    /**
     * Add event delegation for tracking embedded link clicks
     */
    useEffect(() => {
        if (preview || !bodyContentRef) {
            return;
        }

        const container = bodyContentRef;
        container.addEventListener('click', handleEmbeddedLinkClick);

        return () => {
            container.removeEventListener('click', handleEmbeddedLinkClick);
        };
    }, [preview, bodyContentRef]);

    const analyticsInput = {
        eventContainer: type
    };

    useDwellTimeAnalytics(analyticsInput);

    useScrollDepthAnalytics(analyticsInput);

    // Track content viewed event for BWAV-2192
    useContentViewedAnalytics({
        eventContainer: type,
        entryId: entry.id,
        entryUrl: entry.url,
        parentPageType: topicSetId ? 'topic-set' : 'topic',
        parentPageUrl: topicSetId ? topicSetUrl || '' : collectionUrl || ''
    });

    return (
        <>
            {
                <AuthorTools
                    pageId={id}
                    tooltipContent="Edit this topic "
                    confluencePageId={confluencePageId}
                />
            }
            <div
                className="topic section-page flex-column-page"
                data-testid="page-template"
            >
                <div className="content">
                    <Header
                        title={getSupportTitle(context)}
                        id={header.id}
                        pageId={id}
                        cloudProducts={header.cloudProducts}
                        serverProducts={header.serverProducts}
                        additionalLinks={header.additionalLinks}
                        showProductDropdownAsWaffle={true}
                        showPreviewBanner={preview}
                        resources={resources}
                        supportHomePath={
                            isProductContext(context)
                                ? context.deploymentUrls[
                                      context.deployment.toLowerCase()
                                  ] || context.deploymentUrls.default
                                : '/'
                        }
                        collections={collections}
                        logo={header.logo}
                        atlassianSupportLogo={header.atlassianSupportLogo}
                        atlassianLogo={header.atlassianLogo}
                        shouldRenderDocumentation
                        relatedAppReferences={relatedAppReferences}
                        supportFormUrls={{
                            authenticated: isProductContext(context)
                                ? context.customSupportFormAuthenticated
                                : null,
                            unauthenticated: isProductContext(context)
                                ? context.customSupportFormUnauthenticated
                                : null
                        }}
                    />
                    <div className="main-content-container flex-container margin-top-medium">
                        <PageTree
                            context={context}
                            subContentRef={subContentRef}
                        >
                            <PageTreeList isRoot>
                                {pageTree.map(
                                    ({ title, slug, id, childList }) => (
                                        <PageTreeItem
                                            key={id}
                                            title={title}
                                            slug={slug}
                                            id={id}
                                            childList={childList}
                                        />
                                    )
                                )}
                            </PageTreeList>
                        </PageTree>
                        <main className="topic__container">
                            <div
                                className="topic__sub-header"
                                data-testid="topic_sub_header"
                            >
                                <Breadcrumbs
                                    pageTree={pageTree}
                                    entryId={id}
                                    context={context}
                                    type={TOPIC}
                                />
                                {!isThemeContext(context) && (
                                    <DeploymentSelector
                                        selected={context.deployment}
                                        deployments={context.deploymentUrls}
                                        versions={versions}
                                    />
                                )}
                            </div>

                            {context.notice && (
                                <NoticeHub notice={context.notice} />
                            )}

                            <article
                                className="topic__inner-container margin-top-medium"
                                ref={subContentRef}
                                id="maincontent"
                            >
                                <div className="title margin-bottom-medium">
                                    <h1>{topicTitle}</h1>
                                </div>

                                <div className="topic__body">
                                    {isVideoEnabled &&
                                        featuredVideo?.videoUrl && (
                                            <div className="topic__video">
                                                <ComponentVideo
                                                    videoUrl={
                                                        featuredVideo.videoUrl
                                                    }
                                                    title={featuredVideo.title}
                                                    description={
                                                        featuredVideo.description
                                                    }
                                                />
                                            </div>
                                        )}
                                    {preview ? (
                                        updatedBody
                                    ) : (
                                        <div
                                            ref={setBodyContentRef}
                                            dangerouslySetInnerHTML={{
                                                __html: body
                                            }}
                                        />
                                    )}
                                    <FeedbackButtons
                                        productName={
                                            isProductContext(context)
                                                ? context.internalTitle
                                                : getContextName(context)
                                        }
                                        pageId={id}
                                    />
                                    <AdditionalHelp
                                        productNames={[productName]}
                                        deployments={[context.deployment]}
                                    />
                                </div>
                                <Sidebar>
                                    {collectionTree && (
                                        <RightNav
                                            currentPageId={id}
                                            collectionTree={collectionTree}
                                            topicSetRef={topicSetId}
                                            isProgressBarEnabled={
                                                isProgressBarEnabled
                                            }
                                            parentPageType={
                                                topicSetId
                                                    ? 'topic-set'
                                                    : 'topic'
                                            }
                                        />
                                    )}
                                    {url && (
                                        <TableOfContents
                                            links={tableOfContents}
                                            url={url}
                                        />
                                    )}
                                    <Community
                                        contextType={context.type}
                                        productName={productName}
                                    />
                                </Sidebar>
                            </article>
                            {isProgressBarEnabled && (
                                <div className="up-next-container">
                                    <CardGroup
                                        guide={isProgressBarEnabled}
                                        previous={prevGuideCard}
                                        next={nextGuideCard}
                                        otherGuideTopicSets={
                                            otherGuideTopicSets
                                        }
                                    >
                                        {nextGuideCard && (
                                            <CardWithCEM
                                                {...nextGuideCard}
                                                entryId={nextGuideCard.id}
                                                position={1}
                                                parentPageType={
                                                    topicSetId
                                                        ? 'topic-set'
                                                        : 'topic'
                                                }
                                                type="full"
                                            />
                                        )}
                                    </CardGroup>
                                </div>
                            )}
                        </main>
                    </div>
                </div>
                <Footer
                    logo={header.atlassianLogo.url}
                    className={'margin-top-large'}
                />
            </div>
        </>
    );
};

export default SACTopicPage;
